import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { fetchNotices } from '../services/api';
import { useSystemSettings } from '../context/SystemSettingsContext';
import { useTranslation } from '../context/LanguageContext';
import { useAlarmNotification } from '../context/AlarmNotificationContext';
import CollectionStatusIndicator from './CollectionStatusIndicator';
import './Header.css';

/* ===== 상단 헤더 — PNG 시안「헤더 및 팝업」=====
 * 사이드바(로고·메뉴) 오른쪽부터 시작한다. 왼쪽부터
 *  - 공지 박스: 현재 공지 제목 + n/N (10초 롤링). 누르면 공지 팝오버(배지·제목·내용·날짜·조회수, "공지 전체 보기")
 *  - 알람 티커: 심각도 색 테두리 박스 — "CRITICAL n건 | 설비 메시지 | i/N ‹ › ≡". ‹ › 로 미확인 알람을 넘기고(8초 자동),
 *    메시지를 누르면 알람 상세 드로어, ≡ 는 알림센터 목록 드로어
 *  - 오른쪽: 데이터 수집 현황(기존), 전체화면(기존 기능 유지), 종(미확인 수 배지 → 알림센터), 언어(지구본 + 코드)
 * 홈·계정·로그아웃 아이콘은 사이드바 하단 사용자 메뉴로 옮겼다. 알람 데이터는 AlarmNotificationContext 한 곳에서 받는다. */

const TICKER_ROTATE_MS = 8000;
const NOTICE_ROTATE_MS = 10000;

const formatNoticeDate = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`;
};

function Header({ isFullscreen, toggleFullscreen }) {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const { t, language, setLanguage, languages } = useTranslation();
  const { loading: alarmsLoading, recentAlarms, unackedAlarms, counts, openList, openDetail } = useAlarmNotification();

  /* ---- 공지 ---- */
  const [notices, setNotices] = useState([]);
  const [noticeIndex, setNoticeIndex] = useState(0);
  const [showNotices, setShowNotices] = useState(false);
  const noticeRef = useRef(null);

  useEffect(() => {
    fetchNotices()
      .then((data) => setNotices(data || []))
      .catch((err) => { console.error('Failed to load notices:', err); setNotices([]); });
  }, []);

  useEffect(() => {
    if (notices.length <= 1) return undefined;
    const id = setInterval(() => setNoticeIndex((i) => (i + 1) % notices.length), NOTICE_ROTATE_MS);
    return () => clearInterval(id);
  }, [notices.length]);

  useEffect(() => {
    if (!showNotices) return undefined;
    const onDown = (e) => { if (noticeRef.current && !noticeRef.current.contains(e.target)) setShowNotices(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showNotices]);

  const currentNotice = notices[noticeIndex] || null;
  const noticeBadge = (type) => (type === 'urgent' ? t('header.noticeBadge.urgent') : type === 'update' ? t('header.noticeBadge.update') : t('header.noticeBadge.notice'));

  /* ---- 알람 티커: 미확인 알람 우선, 없으면 최근 알람 ---- */
  const tickerAlarms = unackedAlarms.length > 0 ? unackedAlarms : recentAlarms;
  const [tickerIndex, setTickerIndex] = useState(0);
  useEffect(() => { setTickerIndex((i) => (tickerAlarms.length ? Math.min(i, tickerAlarms.length - 1) : 0)); }, [tickerAlarms.length]);
  useEffect(() => {
    if (tickerAlarms.length <= 1) return undefined;
    const id = setInterval(() => setTickerIndex((i) => (i + 1) % tickerAlarms.length), TICKER_ROTATE_MS);
    return () => clearInterval(id);
  }, [tickerAlarms.length]);
  const tickerAlarm = tickerAlarms[tickerIndex] || null;
  const tickerSeverity = tickerAlarm ? tickerAlarm.severity.toLowerCase() : 'none';
  /* "CRITICAL n건" — 미확인 CRITICAL 수. CRITICAL 이 없으면 현재 알람 심각도의 수를 보여 준다 */
  const tickerCountLabel = (() => {
    if (!tickerAlarm) return '';
    if (counts.unackedCritical > 0) return t('header.ticker.count', { sev: 'CRITICAL', count: counts.unackedCritical });
    const n = tickerAlarms.filter((a) => a.severity === tickerAlarm.severity).length;
    return t('header.ticker.count', { sev: tickerAlarm.severity, count: n });
  })();

  /* ---- 언어 드롭다운 (포털) ---- */
  const [showLanguage, setShowLanguage] = useState(false);
  const languageBtnRef = useRef(null);
  const languagePortalRef = useRef(null);
  const [languagePos, setLanguagePos] = useState({ top: 0, right: 0 });
  useEffect(() => {
    if (!showLanguage) return undefined;
    const update = () => {
      if (!languageBtnRef.current) return;
      const rect = languageBtnRef.current.getBoundingClientRect();
      setLanguagePos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    update();
    const onDown = (e) => {
      const inBtn = languageBtnRef.current && languageBtnRef.current.contains(e.target);
      const inPortal = languagePortalRef.current && languagePortalRef.current.contains(e.target);
      if (!inBtn && !inPortal) setShowLanguage(false);
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [showLanguage]);

  return (
    <header className="header">
      {/* 공지 + 알람 티커는 헤더 가운데에 모아 둔다 (시안) */}
      <div className="hd-center">
      {/* ---- 공지 ---- */}
      <div className="hd-notice" ref={noticeRef}>
        <button
          type="button"
          className={`hd-notice-box ${showNotices ? 'open' : ''} ${notices.length === 0 ? 'empty' : ''}`}
          onClick={() => notices.length > 0 && setShowNotices((v) => !v)}
          disabled={notices.length === 0}
        >
          <span className="material-symbols-outlined hd-notice-icon">{currentNotice?.type === 'urgent' ? 'priority_high' : 'campaign'}</span>
          <span className="hd-notice-text">{currentNotice ? currentNotice.title : t('header.noticeEmpty')}</span>
          {notices.length > 1 && <span className="hd-counter">{noticeIndex + 1}/{notices.length}</span>}
        </button>

        {showNotices && (
          <div className="hd-popover" role="dialog" aria-label={t('header.notice')}>
            <div className="hd-popover-head">
              <h3 className="hd-popover-title">{t('header.notice')} <span className="hd-popover-count">{t('header.noticeCount', { count: notices.length })}</span></h3>
              <button type="button" className="hd-icon-btn" onClick={() => setShowNotices(false)} title={t('common.close')} aria-label={t('common.close')}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <ul className="hd-notice-list">
              {notices.map((notice) => (
                <li key={notice.id}>
                  <button type="button" className="hd-notice-item" onClick={() => { setShowNotices(false); navigate('/notices', { state: { openNoticeId: notice.id } }); }}>
                    <div className="hd-notice-item-row">
                      <span className={`hd-notice-badge hd-notice-badge-${notice.type || 'notice'}`}>{noticeBadge(notice.type)}</span>
                      <span className="hd-notice-item-title">{notice.title}</span>
                    </div>
                    {notice.content && <p className="hd-notice-item-body">{notice.content}</p>}
                    <div className="hd-notice-item-meta">
                      {formatNoticeDate(notice.createdAt)}{notice.viewCount != null ? ` · ${t('header.noticeViews', { count: notice.viewCount })}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="hd-popover-foot">
              <button type="button" className="hd-link" onClick={() => { setShowNotices(false); navigate('/notices'); }}>{t('header.noticeAll')}</button>
            </div>
          </div>
        )}
      </div>

      {/* ---- 알람 티커 ---- */}
      {settings.notifyWeb !== false && (
        <div className={`hd-ticker hd-ticker-${tickerSeverity}`}>
          {alarmsLoading && !tickerAlarm ? (
            <span className="hd-ticker-text hd-ticker-muted">{t('header.alarmLoading')}</span>
          ) : !tickerAlarm ? (
            <>
              <span className="material-symbols-outlined hd-ticker-icon">check_circle</span>
              <span className="hd-ticker-text hd-ticker-muted">{t('header.alarmNone')}</span>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined hd-ticker-icon">{tickerAlarm.severity === 'INFO' ? 'info' : 'warning'}</span>
              <span className="hd-ticker-count">{tickerCountLabel}</span>
              <span className="hd-ticker-sep" />
              <button type="button" className="hd-ticker-text" onClick={() => openDetail(tickerAlarm)} title={t('header.ticker.openDetail')}>
                {tickerAlarm.factoryName ? `${tickerAlarm.factoryName} ` : ''}{tickerAlarm.equipmentName} {tickerAlarm.message || tickerAlarm.code}
              </button>
              <span className="hd-counter hd-ticker-counter">{tickerIndex + 1}/{tickerAlarms.length}</span>
              <button type="button" className="hd-ticker-btn" disabled={tickerAlarms.length <= 1} onClick={() => setTickerIndex((i) => (i - 1 + tickerAlarms.length) % tickerAlarms.length)} title={t('header.ticker.prev')} aria-label={t('header.ticker.prev')}>
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button type="button" className="hd-ticker-btn" disabled={tickerAlarms.length <= 1} onClick={() => setTickerIndex((i) => (i + 1) % tickerAlarms.length)} title={t('header.ticker.next')} aria-label={t('header.ticker.next')}>
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
              <button type="button" className="hd-ticker-btn" onClick={openList} title={t('header.ticker.list')} aria-label={t('header.ticker.list')}>
                <span className="material-symbols-outlined">menu</span>
              </button>
            </>
          )}
        </div>
      )}
      </div>

      {/* ---- 오른쪽 ---- */}
      <div className="hd-right">
        <CollectionStatusIndicator />

        <button type="button" className="hd-icon-btn" onClick={toggleFullscreen} title={isFullscreen ? t('common.fullscreenExit') : t('common.fullscreen')} aria-label={isFullscreen ? t('common.fullscreenExit') : t('common.fullscreen')}>
          <span className="material-symbols-outlined">{isFullscreen ? 'fullscreen_exit' : 'fullscreen'}</span>
        </button>

        <button type="button" className="hd-icon-btn hd-bell" onClick={openList} title={t('header.bell')} aria-label={t('header.bell')}>
          <span className="material-symbols-outlined">notifications</span>
          {counts.unacked > 0 && <span className="hd-bell-badge">{counts.unacked > 99 ? '99+' : counts.unacked}</span>}
        </button>

        <button type="button" ref={languageBtnRef} className="hd-lang" onClick={() => setShowLanguage((v) => !v)} title={t('language.label')}>
          <span className="material-symbols-outlined">language</span>
          <span className="hd-lang-code">{String(language || '').toUpperCase()}</span>
        </button>
        {showLanguage && ReactDOM.createPortal(
          <div ref={languagePortalRef} className="hd-lang-menu" style={{ top: `${languagePos.top}px`, right: `${languagePos.right}px` }}>
            <div className="hd-lang-menu-title">{t('language.label')}</div>
            {languages.map((lang) => (
              <button key={lang.code} type="button" className={`hd-lang-option ${language === lang.code ? 'active' : ''}`} onClick={() => { setLanguage(lang.code); setShowLanguage(false); }}>
                <span>{lang.label}</span>
                {language === lang.code && <span className="material-symbols-outlined">check</span>}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>
    </header>
  );
}

export default Header;
